import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CapTableDetailsModel, MeasurementDateName, MeasurementDatesService } from 'api';
import { CapTableVersion } from 'common/types/company';
import { CurrentCompanyMeasurementDate } from 'common/types/measurementDate';
import { CaptableService, CompaniesService } from 'services';
import { useGetPrimaryCapTableByCMD } from 'services/hooks/capTables';
import useGetFinancialStatementsByMD from 'services/hooks/financials/useGetFinancialStatementsByMD/useGetFinancialStatementsByMD';
import { Logger, toString } from 'utilities';
import { UseWaterfallDataParams, WaterfallData } from './useWaterfallData.props';

export type { UseWaterfallDataParams, WaterfallData } from './useWaterfallData.props';

export const useWaterfallData = ({
  companyId,
  measurementDateValue,
  captableId,
  selectedMeasurementDateFromRoute,
  isFirmPage,
}: UseWaterfallDataParams): WaterfallData => {
  // ============================================================
  // State
  // ============================================================
  const [companyMeasurementDates, setCompanyMeasurementDates] = useState<MeasurementDateName[]>([]);
  const [capTableList, setCapTableList] = useState<CapTableVersion[]>([]);
  const [captableDetail, setCaptableDetail] = useState<CapTableDetailsModel | null>(null);

  const [isMeasurementDatesLoading, setIsMeasurementDatesLoading] = useState(false);
  const [isCapTableListLoading, setIsCapTableListLoading] = useState(false);
  const [isCaptableDetailLoading, setIsCaptableDetailLoading] = useState(false);

  // Track current requests to prevent stale responses
  const currentCaptableRequestRef = useRef<number | null>(null);
  const currentCapTableListRequestRef = useRef<string | null>(null);

  // ============================================================
  // Resolve measurement date from date value
  // ============================================================
  const resolvedMeasurementDate = useMemo(() => {
    // COMPANY PAGE: Use route-provided measurement date
    // (On company pages, this is the source of truth - no dropdown)
    if (!isFirmPage) {
      return selectedMeasurementDateFromRoute || undefined;
    }

    // FIRM PAGE: Resolve from the fetched list by matching date value from URL
    // (On firm pages, the URL state is the source of truth)
    if (!measurementDateValue || !companyMeasurementDates.length) {
      return undefined;
    }
    return companyMeasurementDates.find(md => md.date === measurementDateValue);
  }, [isFirmPage, measurementDateValue, companyMeasurementDates, selectedMeasurementDateFromRoute]);

  // ============================================================
  // Fetch Measurement Dates
  // ============================================================
  useEffect(() => {
    if (!companyId) {
      setCompanyMeasurementDates([]);
      return;
    }

    const fetchMeasurementDates = async () => {
      setIsMeasurementDatesLoading(true);
      try {
        const data = await MeasurementDatesService.apiMeasurementDatesCompanyRead(toString(companyId));
        setCompanyMeasurementDates(data || []);
      } catch (error) {
        Logger.error('Error fetching measurement dates:', error);
        setCompanyMeasurementDates([]);
      } finally {
        setIsMeasurementDatesLoading(false);
      }
    };

    fetchMeasurementDates();
  }, [companyId]);

  // ============================================================
  // Fetch Cap Table List
  // ============================================================
  const fetchCapTableList = useCallback(async (compId: number, mdId: number) => {
    const requestKey = `${compId}-${mdId}`;
    currentCapTableListRequestRef.current = requestKey;

    setIsCapTableListLoading(true);
    try {
      const compaSvc = new CompaniesService();
      const list = await compaSvc.getCapTableList(toString(compId), { measurementDateId: toString(mdId) });

      // Only update if this is still the current request
      if (currentCapTableListRequestRef.current === requestKey) {
        setCapTableList(list || []);
      }
    } catch (error) {
      Logger.error('Error fetching cap table list:', error);
      if (currentCapTableListRequestRef.current === requestKey) {
        setCapTableList([]);
      }
    } finally {
      if (currentCapTableListRequestRef.current === requestKey) {
        setIsCapTableListLoading(false);
      }
    }
  }, []);

  // Derive most recent measurement date for fallback
  const mostRecentMeasurementDate = useMemo(() => {
    if (!companyMeasurementDates.length) {
      return undefined;
    }
    return [...companyMeasurementDates].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  }, [companyMeasurementDates]);

  // Auto-fetch cap table list when company + measurement date are available
  // Use fallback to most recent measurement date if no explicit selection
  useEffect(() => {
    const effectiveMeasurementDateId = resolvedMeasurementDate?.id || mostRecentMeasurementDate?.id;

    if (companyId && effectiveMeasurementDateId) {
      fetchCapTableList(companyId, effectiveMeasurementDateId);
    } else {
      setCapTableList([]);
    }
  }, [companyId, resolvedMeasurementDate?.id, mostRecentMeasurementDate?.id, fetchCapTableList]);

  const clearCapTableList = useCallback(() => {
    setCapTableList([]);
    currentCapTableListRequestRef.current = null;
  }, []);

  // ============================================================
  // Fetch Cap Table Detail
  // ============================================================
  const fetchCaptableDetail = useCallback(async (ctId: number) => {
    currentCaptableRequestRef.current = ctId;

    setIsCaptableDetailLoading(true);
    try {
      const captSvc = new CaptableService();
      const data = (await captSvc.getCaptableById(ctId)) as unknown as CapTableDetailsModel;

      // Only update if this is still the current request
      if (currentCaptableRequestRef.current === ctId) {
        setCaptableDetail(data);
      }
    } catch (error) {
      Logger.error('Error fetching cap table detail:', error);
      if (currentCaptableRequestRef.current === ctId) {
        setCaptableDetail(null);
      }
    } finally {
      if (currentCaptableRequestRef.current === ctId) {
        setIsCaptableDetailLoading(false);
      }
    }
  }, []);

  // Auto-fetch cap table detail when captable ID changes
  useEffect(() => {
    if (captableId) {
      fetchCaptableDetail(captableId);
    } else {
      setCaptableDetail(null);
      currentCaptableRequestRef.current = null;
    }
  }, [captableId, fetchCaptableDetail]);

  // ============================================================
  // Derived Values for Defaults
  // ============================================================
  const primaryCaptable = useMemo(() => {
    if (!capTableList.length) {
      return undefined;
    }
    return capTableList.find(ct => ct.is_primary);
  }, [capTableList]);

  // ============================================================
  // Fetch Primary Cap Table (Valuations-style FO source)
  // Skip the dedicated primary fetch when the selected version is already primary.
  // ============================================================
  const companyMeasurementDateForPrimary = useMemo((): CurrentCompanyMeasurementDate | null => {
    const measurementDate = resolvedMeasurementDate ?? mostRecentMeasurementDate;
    const cmdId = measurementDate?.cmd_id;
    if (!cmdId || !companyId) {
      return null;
    }

    return {
      id: cmdId,
      md_id: measurementDate?.id ?? null,
      name: measurementDate?.name ?? '',
      slug: measurementDate?.slug ?? '',
      date: measurementDate?.date ?? '',
      is_open: measurementDate?.is_open ?? false,
      is_ready: measurementDate?.is_ready ?? false,
      company_id: measurementDate?.company_id ?? companyId,
      company_name: '',
    };
  }, [resolvedMeasurementDate, mostRecentMeasurementDate, companyId]);

  const selectedIsPrimary = Boolean(captableId && primaryCaptable?.id === captableId);
  // captableDetail updates async on version switch — only alias when it matches selection,
  // otherwise briefly treating a stale non-primary detail as primary FO.
  const captableDetailMatchesSelection = captableDetail?.id === captableId;

  const { data: fetchedPrimaryCapTableDetail, isLoading: isFetchedPrimaryCapTableLoading } = useGetPrimaryCapTableByCMD(
    {
      companyMeasurementDate: companyMeasurementDateForPrimary,
      shouldQueryAutomatically: Boolean(companyMeasurementDateForPrimary) && !selectedIsPrimary,
    }
  );

  const primaryCapTableDetail =
    selectedIsPrimary && captableDetailMatchesSelection ? captableDetail : (fetchedPrimaryCapTableDetail ?? null);

  const isPrimaryCapTableLoading = selectedIsPrimary
    ? isCaptableDetailLoading || !captableDetailMatchesSelection
    : isFetchedPrimaryCapTableLoading;

  // ============================================================
  // Effective Fund Ownership Source
  // Prefer the selected cap table's own fund ownership when it has one
  // (pro forma versions can now carry their own FO); otherwise fall back to
  // the primary cap table, same as before this version had its own FO.
  // ============================================================
  const hasOwnFundOwnership = Boolean(
    captableDetailMatchesSelection && (captableDetail?.fund_ownership?.fund_ownership_detail?.length ?? 0) > 0
  );

  const fundOwnershipCapTableDetail = hasOwnFundOwnership ? captableDetail : primaryCapTableDetail;

  // ============================================================
  // Fetch Financials
  // ============================================================
  const { data: financialsData, isLoading: isFinancialsLoading } = useGetFinancialStatementsByMD({
    // Type cast needed due to mismatch between MeasurementDateName and CurrentCompanyMeasurementDate
    measurementDate: resolvedMeasurementDate as Parameters<typeof useGetFinancialStatementsByMD>[0]['measurementDate'],
    shouldQueryAutomatically: !!resolvedMeasurementDate,
  });

  const financials = useMemo(() => {
    if (financialsData?.length) {
      const currentFinancials = financialsData[0];
      const { ltm } = currentFinancials;
      if (ltm) {
        const { balance_sheet } = ltm;
        return {
          plusCash: Number(balance_sheet?.total_cash_equivalents || 0),
          lessDebt: Number(balance_sheet?.total_debt || 0),
        };
      }
    }
    return { plusCash: 0, lessDebt: 0 };
  }, [financialsData]);

  // ============================================================
  // Combined Loading State
  // ============================================================
  const isLoading =
    isMeasurementDatesLoading ||
    isCapTableListLoading ||
    isCaptableDetailLoading ||
    (!selectedIsPrimary && isPrimaryCapTableLoading) ||
    isFinancialsLoading;

  return {
    // Fetched data
    companyMeasurementDates,
    capTableList,
    captableDetail,
    primaryCapTableDetail,
    fundOwnershipCapTableDetail,
    financials,

    // Loading states
    isMeasurementDatesLoading,
    isCapTableListLoading,
    isCaptableDetailLoading,
    isPrimaryCapTableLoading,
    isFinancialsLoading,
    isLoading,

    // Derived values
    primaryCaptable,
    mostRecentMeasurementDate,
    resolvedMeasurementDate,

    // Manual refetch functions
    refetchCapTableList: fetchCapTableList,
    refetchCaptableDetail: fetchCaptableDetail,
    clearCapTableList,
  };
};
